import { Button, ButtonProps } from "@mui/material"
import { setOpen } from "components/shopFeatures/auth/authSlice"
import { useLayoutContext } from "layout/oldLayout/LayoutContext"
import { useRouter } from "next/navigation"
import { Link, LinkProps, useLocation } from "react-router-dom"
import routes from "routes"
import { useAppDispatch, useTypedSelector } from "store"
import styled from "styled-components"
import colors from "theme/colors"
import { StyledRow, TypographyCenter } from "theme/shared"
import OnlineBenefits from "../auth/OnlineBenefits/OnlineBenefits"
import { Font } from "../wizard/constants"
import useInStock from "../wizard/useInStock"
import { setIsDialogOpen } from "../wizard/wizardSlice"

const StyledContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  gap: 4px;

  ${(p) => p.theme.breakpoints.down("sm")} {
    gap: 8px;
    position: fixed;
    padding: 5px;
    background: white;
    border-radius: 5px;
    width: calc(100% - 32px);
    left: 16px;
    bottom: 25px;
    z-index: 999;
    box-shadow: 0px -1px 4px 0px #00000014, 0px 4px 4px 0px #00000040;
  }
`

const StyledButtonBaseBlank = styled(Button)`
  border-radius: 3px;
  height: 45px;
  width: 48%;
  flex-grow: 1;

  ${(p) => p.theme.breakpoints.down("sm")} {
    width: 50%;
  }

  &[class*="MuiButton-text"] {
    padding: 0;
  }
`

const StyledFakeButton = styled.div<{ $dark?: boolean }>`
  ${(p) => p?.$dark && `background-color: ${p.theme.colors.black};`}
  border-radius: 3px;
  height: 45px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  cursor: pointer;
  border: ${(p) =>
    p?.$dark ? "none" : `2px solid ${p?.theme?.colors?.black}`};
`

const StyledTypography = styled(TypographyCenter)`
  font-size: 16px;
  font-family: ${Font.OENaPro};
  font-weight: 400;
  line-height: 1.1;
`

type StyledButtonLinkProps = React.FC<ButtonProps & LinkProps>

const StyledButtonLink = styled<StyledButtonLinkProps>(Button).attrs({
  component: Link,
})`
  border-radius: 3px;
  background: black;
  color: white;
  width: 100%;
  height: 45px;
  border: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 19.2px;
  letter-spacing: 0.05em;
  font-family: ${Font.OENaPro};

  :hover {
    background: #999;
    border: 0;
  }
`

type SummaryButtonsProps = {
  className?: string
}

const SummaryButtons = ({
  className,
}: SummaryButtonsProps): JSX.Element | null => {
  const router = useRouter()
  const dispatch = useAppDispatch()

  const { auth, wizard } = useTypedSelector((state) => state)
  const isInStock = useInStock()

  const isPreSale = wizard.model?.typeId === 3

  const { pathname } = useLocation()
  const activeStepIndex = routes.array.findIndex((r) => r.path === pathname)
  const nextStep = routes.array[activeStepIndex + 1]

  const { focusMain } = useLayoutContext()

  if (!wizard?.model?.id) return null

  const { authenticated } = auth

  return (
    <StyledContainer className={className}>
      <StyledButtonBaseBlank
        onClick={() => {
          if (authenticated) {
            nextStep.path === routes.summary.path
            focusMain()
          } else {
            dispatch(setOpen(true))
            dispatch(setIsDialogOpen(true))
          }
        }}
      >
        {authenticated ? (
          <StyledButtonLink
            variant="outlined"
            color="primary"
            to={nextStep.path}
            role="link"
          >
            {!isInStock
              ? "להשארת פרטים"
              : isPreSale
              ? "תשלום מקדמה"
              : "להזמנה ותשלום מקדמה"}
          </StyledButtonLink>
        ) : (
          <StyledFakeButton>
            <StyledTypography variant="body2">
              {isPreSale ? "הטבה מיוחדת" : "להטבה בלעדית באתר"}
            </StyledTypography>
          </StyledFakeButton>
        )}
        <OnlineBenefits />
      </StyledButtonBaseBlank>
      {!authenticated && (
        <>
          {!isInStock ? (
            <StyledButtonBaseBlank
              onClick={() => router.push("/contact-us?subject=קניית רכב")}
            >
              <StyledFakeButton $dark>
                <StyledRow $fullWidth $justify="center" $gap="4px">
                  <StyledTypography variant="body2" $color={colors.white}>
                    להשארת פרטים
                  </StyledTypography>
                </StyledRow>
              </StyledFakeButton>
            </StyledButtonBaseBlank>
          ) : (
            <StyledButtonBaseBlank
              onClick={() => {
                dispatch(setOpen(true))
                if (authenticated) {
                  nextStep.path === routes.summary.path
                }
                dispatch(setIsDialogOpen(true))
              }}
            >
              <StyledFakeButton $dark>
                <StyledTypography variant="body2" $color={colors.white}>
                  {isPreSale ? "תשלום מקדמה" : "להזמנה ותשלום מקדמה"}
                </StyledTypography>
              </StyledFakeButton>
            </StyledButtonBaseBlank>
          )}
        </>
      )}
    </StyledContainer>
  )
}

export default SummaryButtons
